import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import Device from '../components/Device';
import EnterpriseDeviceCluster from '../components/EnterpriseDeviceCluster';
import AssetPointsModal from '../components/AssetPointsModal';
import '../styles/portfolio.css';
import * as api from '../services/api';
import * as signatureService from '../services/signatureService';

/**
 * Portfolio/Asset Management Page
 * Displays user's owned RWA devices with stats and management options
 */
const PortfolioPage = () => {
  const { wallet } = useWallet();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('assets'); // 'assets' or 'orders'
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [totalRewards, setTotalRewards] = useState(0);
  const [portfolioStats, setPortfolioStats] = useState({
    totalAssets: 0,
    totalValue: 0,
    monthlyYield: 0,
    averageAPY: 0
  });
  
  // Points data state
  const [pointsBalance, setPointsBalance] = useState({
    currentBalance: 0,
    totalEarned: 0,
    loading: false,
    error: null
  });

  const [assetPointsMap, setAssetPointsMap] = useState({});
  
  // Modal state
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showPointsModal, setShowPointsModal] = useState(false);

  // Fetch user's assets and orders when wallet changes
  useEffect(() => {
    if (wallet.connected) {
      fetchUserRwaAssets();
    } else {
      setAssets([]);
      setOrders([]);
      setIsLoading(false);
    }
  }, [wallet]);

  // Fetch user's RWA assets from API using signatureService
  const fetchUserRwaAssets = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get network ID based on wallet network
      const networkId = wallet.network === 'solana' ? 3 : (wallet.network === 'bsc' ? 2 : 1);
      
      // Get signature using signature service
      const signatureData = await signatureService.getValidSignature(
        wallet.address,
        networkId,
        wallet.type
      );
      
      // Call the user RWA assets endpoint
      const response = await fetch('https://api.aeronyx.network/api/payments/user-rwa-assets/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: wallet.address,
          blockchain_network_id: networkId,
          signature: signatureData.signature,
          message: signatureData.message,
          wallet_type: wallet.type
        }),
      });
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch RWA assets');
      }
      
      // Process the API response
      const assetsData = result.data?.assets || [];
      const ordersData = result.data?.orders || [];
      
      // Transform assets data format if needed
      const transformedAssets = assetsData.map(asset => ({
        id: asset.id,
        name: asset.product_name || `${getAssetType(asset.product_id)} Device`,
        serial_number: asset.serial_number || `RWA-${asset.order_id.slice(-6)}`,
        type: getAssetType(asset.product_id),
        tier: getAssetTier(asset.product_id),
        status: asset.status || 'active',
        value: asset.amount || 0,
        current_apy: asset.current_apy || getAssetAPY(asset.product_id),
        mining_factor: asset.mining_factor || getAssetMiningFactor(asset.product_id),
        total_rewards: asset.total_rewards || 0,
        created_at: asset.created_at,
        payment_confirmed_at: asset.payment_confirmed_at,
        order_id: asset.order_id // Store order_id for points lookup
      }));
      
      setAssets(transformedAssets);
      setOrders(ordersData);
      
      // Calculate portfolio statistics
      const stats = calculatePortfolioStats(transformedAssets);
      setPortfolioStats(stats);
      setTotalRewards(transformedAssets.reduce((sum, asset) => sum + (asset.total_rewards || 0), 0));

      // Now fetch points data if assets are loaded
      if (transformedAssets.length > 0) {
        fetchPointsBalance(signatureData);
      }
    } catch (error) {
      console.error('Error fetching RWA assets:', error);
      setError(`Failed to load your portfolio: ${error.message}`);
      
      // Clear signature cache if we get an auth error
      if (error.message.includes('signature') || error.message.includes('auth')) {
        signatureService.clearSignatureCache();
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch points balance from the API using cached signature when possible
  const fetchPointsBalance = async (existingSignatureData = null) => {
    try {
      setPointsBalance(prev => ({ ...prev, loading: true, error: null }));
      
      // Get network ID based on wallet network
      const networkId = wallet.network === 'solana' ? 3 : (wallet.network === 'bsc' ? 2 : 1);
      
      // Use existing signature data if provided, otherwise get a new one
      const signatureData = existingSignatureData || 
        await signatureService.getValidSignature(wallet.address, networkId, wallet.type);
      
      // Call the points balance endpoint
      const response = await fetch('https://api.aeronyx.network/api/points/balance/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: wallet.address,
          blockchain_network_id: networkId,
          signature: signatureData.signature,
          message: signatureData.message,
          wallet_type: wallet.type
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Points API request failed: ${response.status}`);
      }
      
      const pointsResult = await response.json();
      
      if (!pointsResult.success) {
        throw new Error(pointsResult.message || 'Failed to fetch points balance');
      }
      
      console.log('Points data:', pointsResult.data);
      
      // Set the total points balance
      setPointsBalance({
        currentBalance: pointsResult.data?.current_balance || 0,
        totalEarned: pointsResult.data?.total_earned || 0,
        loading: false,
        error: null
      });
      
      // Create a map of order_id to points data
      // First check if we have assets array
      if (pointsResult.data?.assets && Array.isArray(pointsResult.data.assets) && pointsResult.data.assets.length > 0) {
        const pointsMap = {};
        pointsResult.data.assets.forEach(assetPoint => {
          if (assetPoint.order_id) {
            pointsMap[assetPoint.order_id] = {
              netPointsReceived: assetPoint.net_points_received || 0,
              totalPointsGenerated: assetPoint.total_points_generated || 0,
              todayGeneration: assetPoint.today_generation_net || 0,
              expectedHourlyRate: assetPoint.expected_hourly_rate_net || 0
            };
          }
        });
        
        setAssetPointsMap(pointsMap);
      }
      // If assets array is empty, try to extract data from recent_transactions
      else if (pointsResult.data?.recent_transactions && Array.isArray(pointsResult.data.recent_transactions) && pointsResult.data.recent_transactions.length > 0) {
        // Group transactions by order_id
        const transactionsByOrder = {};
        
        pointsResult.data.recent_transactions.forEach(transaction => {
          if (transaction.source_order && transaction.source_order.order_id) {
            const orderId = transaction.source_order.order_id;
            
            if (!transactionsByOrder[orderId]) {
              transactionsByOrder[orderId] = {
                transactions: [],
                productId: transaction.source_order.product_id,
                productName: transaction.source_order.product_name
              };
            }
            
            transactionsByOrder[orderId].transactions.push(transaction);
          }
        });
        
        // Calculate points for each order
        const pointsMap = {};
        for (const [orderId, data] of Object.entries(transactionsByOrder)) {
          // Sum up completed transactions
          let netPointsReceived = 0;
          let totalPointsGenerated = 0;
          let todayGeneration = 0;
          
          // Get today's date for comparison
          const today = new Date().toDateString();
          
          data.transactions.forEach(tx => {
            if (tx.status === 'completed') {
              totalPointsGenerated += tx.amount || 0;
              netPointsReceived += tx.net_amount || 0;
              
              // Check if transaction is from today
              const txDate = new Date(tx.created_at).toDateString();
              if (txDate === today) {
                todayGeneration += tx.net_amount || 0;
              }
            }
          });
          
          // Calculate hourly rate based on latest transaction if available
          let expectedHourlyRate = 0;
          const completedTransactions = data.transactions.filter(tx => tx.status === 'completed');
          if (completedTransactions.length > 0) {
            // Sort by date and get the latest
            completedTransactions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            expectedHourlyRate = completedTransactions[0].net_amount || 0;
          }
          
          pointsMap[orderId] = {
            netPointsReceived,
            totalPointsGenerated,
            todayGeneration,
            expectedHourlyRate
          };
        }
        
        setAssetPointsMap(pointsMap);
      }
      
    } catch (error) {
      console.error('Error fetching points balance:', error);
      setPointsBalance(prev => ({ 
        ...prev, 
        loading: false,
        error: error.message
      }));
      
      // Clear signature cache if we get an auth error
      if (error.message.includes('signature') || error.message.includes('auth')) {
        signatureService.clearSignatureCache();
      }
    }
  };

  // Helper functions to get asset properties based on product_id
  const getAssetType = (productId) => {
    if (!productId) return 'basic';
    return productId.toLowerCase();
  };

  const getAssetTier = (productId) => {
    const tierMap = {
      'enterprise': 'Enterprise',
      'elite': 'Elite',
      'pro': 'Pro',
      'basic': 'Basic'
    };
    return tierMap[productId?.toLowerCase()] || 'Basic';
  };

  const getAssetAPY = (productId) => {
    const apyMap = {
      'enterprise': 60,
      'elite': 50,
      'pro': 40,
      'basic': 30
    };
    return apyMap[productId?.toLowerCase()] || 30;
  };

  const getAssetMiningFactor = (productId) => {
    const factorMap = {
      'enterprise': 6,
      'elite': 3,
      'pro': 2,
      'basic': 1
    };
    return factorMap[productId?.toLowerCase()] || 1;
  };

  // Calculate portfolio statistics from assets
  const calculatePortfolioStats = (assets) => {
    if (!assets || assets.length === 0) {
      return {
        totalAssets: 0,
        totalValue: 0,
        monthlyYield: 0,
        averageAPY: 0
      };
    }

    const totalAssets = assets.length;
    const totalValue = assets.reduce((sum, asset) => sum + (asset.value || 0), 0);
    const totalAPY = assets.reduce((sum, asset) => sum + (asset.current_apy || 0), 0);
    const averageAPY = totalAssets ? totalAPY / totalAssets : 0;
    const monthlyYield = totalValue * (averageAPY / 12) / 100;

    return {
      totalAssets,
      totalValue,
      monthlyYield,
      averageAPY
    };
  };

  // Filter assets by type
  const getFilteredAssets = () => {
    if (activeFilter === 'all') {
      return assets;
    }
    return assets.filter(asset => asset.type === activeFilter);
  };

  // Handle connect wallet click
  const handleConnectWallet = () => {
    // Open wallet modal
    if (wallet.openWalletModal) {
      wallet.openWalletModal();
    }
  };

  // Open points details modal
  const openPointsDetails = (asset) => {
    setSelectedAsset(asset);
    setShowPointsModal(true);
  };

  // Format currency for display
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0.00';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format number with commas
  const formatNumber = (value) => {
    if (value === undefined || value === null) return '0';
    
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short', 
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Get status class for order status styling
  const getStatusClass = (status) => {
    if (!status) return 'status-unknown';
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('success') || statusLower.includes('complete')) {
      return 'status-success';
    } else if (statusLower.includes('pending') || statusLower.includes('process')) {
      return 'status-pending';
    } else if (statusLower.includes('fail') || statusLower.includes('cancel') || statusLower.includes('reject')) {
      return 'status-failed';
    }
    return 'status-unknown';
  };

  // Render empty state for when user has no assets
  const renderEmptyState = () => (
    <div className="portfolio-empty-state">
      <div className="empty-state-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 11H5C3.89543 11 3 11.8954 3 13V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V13C21 11.8954 20.1046 11 19 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7 11V7C7 5.93913 7.42143 4.92172 8.17157 4.17157C8.92172 3.42143 9.93913 3 11 3H13C14.0609 3 15.0783 3.42143 15.8284 4.17157C16.5786 4.92172 17 5.93913 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="16" r="1" fill="currentColor"/>
        </svg>
      </div>
      <h3>No {activeTab === 'assets' ? 'Assets' : 'Orders'} Found</h3>
      <p>You don't have any AeroFi RWA devices {activeTab === 'assets' ? '' : 'orders'} yet.</p>
      <button className="btn" onClick={() => navigate('/products')}>Browse Devices</button>
    </div>
  );

  // Render wallet disconnected state
  const renderWalletDisconnected = () => (
    <div className="portfolio-empty-state">
      <div className="empty-state-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 7L12 3L4 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M4 7V17L12 21L20 17V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11L4 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <h3>Wallet Not Connected</h3>
      <p>Connect your wallet to view your AeroFi RWA devices.</p>
      <button className="btn" onClick={handleConnectWallet}>Connect Wallet</button>
    </div>
  );

  // Render asset card
  const renderAssetCard = (asset) => {
    // Get points data for this asset if available
    const assetPoints = asset.order_id ? assetPointsMap[asset.order_id] : null;
    
    return (
      <div className="asset-card" key={asset.id}>
        <div className="asset-header">
          <div className="asset-label">{asset.tier.toUpperCase()}</div>
          <div className="asset-status-badge">{asset.status}</div>
        </div>
        
        <div className="asset-device-container">
          {asset.type === 'enterprise' ? (
            <EnterpriseDeviceCluster />
          ) : (
            <Device type={asset.type} />
          )}
        </div>
  
        <div className="asset-info">
          <h3 className="asset-name">{asset.name}</h3>
          <div className="asset-id">Order: #{asset.order_id ? asset.order_id.slice(-6) : 'N/A'}</div>
        </div>
        
        <div 
          className="asset-rewards"
          onClick={() => assetPoints && openPointsDetails(asset)}
          style={assetPoints ? {cursor: 'pointer'} : {}}
        >
          <div className="rewards-label">Total Points</div>
          {pointsBalance.loading ? (
            <div className="rewards-value loading">
              <div className="spinner small"></div>
              <span>Loading...</span>
            </div>
          ) : assetPoints ? (
            <>
              <div className="rewards-value">{formatNumber(assetPoints.netPointsReceived)}</div>
              {assetPoints.todayGeneration > 0 && (
                <div className="rewards-today">
                  <span>Today: +{formatNumber(assetPoints.todayGeneration)}</span>
                </div>
              )}
              <div className="view-details-hint">Click to view details</div>
            </>
          ) : (
            <div className="rewards-value no-data">No data available</div>
          )}
        </div>
        
        <div className="asset-actions">
          <button 
            className="btn" 
            onClick={() => navigate(`/device/order/${asset.order_id}`)}
          >
            Manage Device
          </button>
        </div>
      </div>
    );
  };

  // Render order card
  const renderOrderCard = (order) => (
    <div className="order-card" key={order.id || order.order_id}>
      <div className="order-header">
        <div className="order-id">Order #{order.order_id || order.id}</div>
        <div className={`order-status ${getStatusClass(order.status)}`}>
          {order.status || 'Unknown'}
        </div>
      </div>
      
      <div className="order-info">
        <div className="order-product">
          <div className="info-label">Product</div>
          <div className="info-value">{order.product_name || 'RWA Device'}</div>
        </div>
        
        <div className="order-date">
          <div className="info-label">Date</div>
          <div className="info-value">{formatDate(order.created_at)}</div>
        </div>
        
        <div className="order-amount">
          <div className="info-label">Amount</div>
          <div className="info-value">{formatCurrency(order.amount || 0)}</div>
        </div>
        
        {order.selected_network && (
          <div className="order-network">
            <div className="info-label">Network</div>
            <div className="info-value">{order.selected_network}</div>
          </div>
        )}
      </div>
      
      <div className="order-actions">
        <button 
          className="btn" 
          onClick={() => navigate(`/orders/${order.order_id || order.id}`)}
        >
          View Details
        </button>
      </div>
    </div>
  );

  // Handle refresh button click
  const handleRefresh = () => {
    // Clear signature cache to force new signature
    signatureService.clearSignatureCache();
    // Fetch data again
    fetchUserRwaAssets();
  };

  return (
    <div className="portfolio-page portfolio-device-scope">
      <div className="container">
        <header className="portfolio-header">
          <h1>My <span>Portfolio</span></h1>
          <p>Manage your AeroFi RWA devices and track performance</p>
        </header>
        
        {!wallet.connected ? (
          renderWalletDisconnected()
        ) : isLoading ? (
          <div className="portfolio-loading">
            <div className="spinner"></div>
            <p>Loading your portfolio...</p>
          </div>
        ) : error ? (
          <div className="portfolio-error">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
              <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>{error}</p>
            <button className="btn" onClick={handleRefresh}>Try Again</button>
          </div>
        ) : (
          <>
            {/* Tab navigation */}
            <div className="portfolio-tabs">
              <button 
                className={`tab-btn ${activeTab === 'assets' ? 'active' : ''}`}
                onClick={() => setActiveTab('assets')}
              >
                Assets
              </button>
              <button 
                className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
                onClick={() => setActiveTab('orders')}
              >
                Orders
              </button>
              <button 
                className="refresh-btn"
                onClick={handleRefresh}
                title="Refresh data"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M23 4V10H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M1 20V14H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3.51 9.00001C4.01717 7.56662 4.87913 6.2789 6.01547 5.27546C7.1518 4.27202 8.52547 3.58965 10.0083 3.29125C11.4911 2.99284 13.0348 3.09093 14.4761 3.57516C15.9175 4.05939 17.2119 4.90973 18.24 6.04001L23 10M1 14L5.76 17.96C6.78811 19.0903 8.08254 19.9406 9.52387 20.4249C10.9652 20.9091 12.5089 21.0072 13.9917 20.7088C15.4745 20.4104 16.8482 19.728 17.9845 18.7245C19.1209 17.7211 19.9828 16.4334 20.49 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Asset view */}
            {activeTab === 'assets' && (
              <>
                {assets.length > 0 ? (
                  <>
                    <div className="portfolio-filters">
                      <button 
                        className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('all')}
                      >
                        All Devices
                      </button>
                      <button 
                        className={`filter-btn ${activeFilter === 'basic' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('basic')}
                      >
                        Basic
                      </button>
                      <button 
                        className={`filter-btn ${activeFilter === 'pro' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('pro')}
                      >
                        Pro
                      </button>
                      <button 
                        className={`filter-btn ${activeFilter === 'elite' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('elite')}
                      >
                        Elite
                      </button>
                      <button 
                        className={`filter-btn ${activeFilter === 'enterprise' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('enterprise')}
                      >
                        Enterprise
                      </button>
                    </div>

                    <div className="assets-grid">
                      {getFilteredAssets().map(asset => renderAssetCard(asset))}
                    </div>
                  </>
                ) : (
                  renderEmptyState()
                )}
              </>
            )}
            
            {/* Orders view */}
            {activeTab === 'orders' && (
              <>
                {orders.length > 0 ? (
                  <div className="orders-list">
                    {orders.map(order => renderOrderCard(order))}
                  </div>
                ) : (
                  renderEmptyState()
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Asset Points Modal */}
      <AssetPointsModal 
        isOpen={showPointsModal} 
        onClose={() => setShowPointsModal(false)} 
        assetData={selectedAsset}
        walletData={wallet}
      />
    </div>
  );
};

export default PortfolioPage;
